/**
 * Competitor Section Component
 * Displays competitor rankings and visibility scores
 */

import React from 'react';

interface CompetitorData {
    empty_state: boolean;
    your_domain?: string;
    your_rank?: number;
    your_score?: number;
    total_competitors?: number;
    rankings?: Array<{
        domain: string;
        visibility_score: number;
        rank: number;
        is_user: boolean;
    }>;
    gap_analysis?: {
        gap_to_leader: number;
        gap_to_average: number;
        position_summary: string;
    };
    message?: string;
}

interface CompetitorSectionProps {
    data: CompetitorData | null;
    emptyState: boolean;
}

const CompetitorSection: React.FC<CompetitorSectionProps> = ({ data, emptyState }) => {
    if (emptyState || data?.empty_state) {
        return (
            <div className="competitor-section empty">
                <h2>Competitor Analysis</h2>
                <div className="empty-state-box">
                    <span className="empty-icon">🏆</span>
                    <h3>No Competitors Configured</h3>
                    <p>{data?.message || 'Add competitors during onboarding to see visibility rankings'}</p>
                    <button className="add-competitor-btn">Add Competitors</button>
                </div>
            </div>
        );
    }

    if (!data) {
        return null;
    }

    return (
        <div className="competitor-section">
            <h2>Competitor Analysis</h2>

            {/* Rankings Table */}
            <div className="rankings-table">
                <div className="table-header">
                    <span className="col-rank">Rank</span>
                    <span className="col-domain">Domain</span>
                    <span className="col-score">Visibility Score</span>
                </div>
                <div className="table-body">
                    {data.rankings?.map((item) => (
                        <div
                            key={item.domain}
                            className={`table-row ${item.is_user ? 'is-user' : ''}`}
                        >
                            <span className="col-rank">#{item.rank}</span>
                            <span className="col-domain">
                                {item.domain}
                                {item.is_user && <span className="user-badge">You</span>}
                            </span>
                            <span className="col-score">
                                <div className="score-bar">
                                    <div
                                        className="score-fill"
                                        style={{ width: `${item.visibility_score}%` }}
                                    ></div>
                                </div>
                                <span className="score-value">{item.visibility_score.toFixed(1)}</span>
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Gap Analysis */}
            {data.gap_analysis && (
                <div className="gap-analysis">
                    <h3>Gap Analysis</h3>
                    <p className="gap-summary">{data.gap_analysis.position_summary}</p>
                    <div className="gap-metrics">
                        <div className="gap-metric">
                            <span className="gap-label">Gap to Leader</span>
                            <span className={`gap-value ${data.gap_analysis.gap_to_leader > 0 ? 'negative' : 'positive'}`}>
                                {data.gap_analysis.gap_to_leader > 0 ? '-' : '+'}{Math.abs(data.gap_analysis.gap_to_leader).toFixed(1)}
                            </span>
                        </div>
                        <div className="gap-metric">
                            <span className="gap-label">Gap to Average</span>
                            <span className={`gap-value ${data.gap_analysis.gap_to_average > 0 ? 'negative' : 'positive'}`}>
                                {data.gap_analysis.gap_to_average > 0 ? '-' : '+'}{Math.abs(data.gap_analysis.gap_to_average).toFixed(1)}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompetitorSection;
